import { useState } from 'react'

import { useDisplaySources } from './useDisplaySources'
import { DocumentViewer } from './DocumentViewer'
import { DocumentThumbnail } from './DocumentThumbnail'
import { PdfPager } from './PdfPager'
import { ViewerToolbar } from './ViewerToolbar'
import { FullscreenViewer } from './FullscreenViewer'
import { useZoomPan } from './useZoomPan'
import { getSavedFlip, getSavedRotation, saveFlip, saveRotation } from './rotation'

type HistoryFilesProps = {
  historyId: string
  browserFileNames: string[]
}

export const HistoryFiles = ({ historyId, browserFileNames }: HistoryFilesProps) => {
  const files = useDisplaySources(browserFileNames)

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [numPages, setNumPages] = useState(1)
  const [fullscreenOpen, setFullscreenOpen] = useState(false)
  const zoomPan = useZoomPan()

  const current = files[selectedIndex]
  const [rotate, setRotate] = useState(() => {
    const first = files[0]
    return first
      ? getSavedRotation(historyId, first.src, first.isPdf ? 1 : undefined)
      : 0
  })
  const [flipX, setFlipX] = useState(() => {
    const first = files[0]
    return first ? getSavedFlip(historyId, first.src, first.isPdf ? 1 : undefined) : false
  })

  const selectDoc = (index: number) => {
    const f = files[index]
    if (!f) return
    zoomPan.setAnimate(false)
    zoomPan.reset()
    setSelectedIndex(index)
    setPageNumber(1)
    setNumPages(1)
    setRotate(getSavedRotation(historyId, f.src, f.isPdf ? 1 : undefined))
    setFlipX(getSavedFlip(historyId, f.src, f.isPdf ? 1 : undefined))
  }

  const changePage = (p: number) => {
    if (!current) return
    zoomPan.setAnimate(false)
    zoomPan.reset()
    setPageNumber(p)
    setRotate(getSavedRotation(historyId, current.src, p))
    setFlipX(getSavedFlip(historyId, current.src, p))
  }

  const handleFlip = () => {
    if (!current) return
    const next = !flipX
    setFlipX(next)
    saveFlip(historyId, current.src, next, current.isPdf ? pageNumber : undefined)
  }

  const handleRotate = (delta: number) => {
    if (!current) return
    zoomPan.setAnimate(true)
    const next = rotate + delta
    setRotate(next)
    saveRotation(historyId, current.src, next, current.isPdf ? pageNumber : undefined)
  }

  const closeFullscreen = () => {
    setFullscreenOpen(false)
    if (current) {
      setRotate(
        getSavedRotation(historyId, current.src, current.isPdf ? pageNumber : undefined),
      )
      setFlipX(getSavedFlip(historyId, current.src, current.isPdf ? pageNumber : undefined))
    }
  }

  if (!browserFileNames?.length || !current) return null

  return (
    <>
      <div style={{ display: 'flex', gap: 12 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            overflowY: 'auto',
            maxHeight: 500,
          }}
        >
          {files.map((f, index) => (
            <DocumentThumbnail
              key={f.src}
              src={f.displaySrc}
              status={f.status}
              isPdf={f.isPdf}
              selected={index === selectedIndex}
              rotate={
                index === selectedIndex
                  ? rotate
                  : getSavedRotation(historyId, f.src, f.isPdf ? 1 : undefined)
              }
              onClick={() => selectDoc(index)}
            />
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ marginBottom: 12 }}>
            <DocumentViewer
              src={current.displaySrc}
              status={current.status}
              isPdf={current.isPdf}
              pageNumber={pageNumber}
              rotate={rotate}
              flipX={flipX}
              zoomPan={zoomPan}
              height={500}
              onClick={() => setFullscreenOpen(true)}
              onPdfLoad={setNumPages}
            />
          </div>
          {current.isPdf && numPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
              <PdfPager page={pageNumber} numPages={numPages} onChange={changePage} />
            </div>
          )}
          <ViewerToolbar
            scale={zoomPan.scale}
            onZoom={(d) => zoomPan.setScaleAnimated(zoomPan.scale + d)}
            onRotateLeft={() => handleRotate(-90)}
            onRotateRight={() => handleRotate(90)}
            isFlipped={flipX}
            onFlip={handleFlip}
          />
        </div>
      </div>
      <FullscreenViewer
        open={fullscreenOpen}
        onClose={closeFullscreen}
        historyId={historyId}
        files={files}
        index={selectedIndex}
        page={pageNumber}
        numPages={numPages}
        onIndexChange={selectDoc}
        onPageChange={changePage}
        onPdfLoad={setNumPages}
      />
    </>
  )
}
